import { getCurrentReader } from "../auth/getCurrentReader";

const API_BASE = process.env.REACT_APP_TBR_API_BASE;
export class TbrAuthError extends Error {
  constructor() { super("Sign in as a reader to save books to your TBR."); this.code = "READER_SIGN_IN_REQUIRED"; }
}
export async function resolveTbrReader() {
  let timer;
  const reader = await Promise.race([getCurrentReader(), new Promise((_, reject) => { timer = setTimeout(() => reject(new Error("Your Reader session took too long to load. Please retry.")), 20000); })]).finally(() => clearTimeout(timer));
  if (!reader?.readerSub || !reader?.idToken) throw new TbrAuthError();
  return reader;
}
export class TbrAccountChangedError extends Error {
  constructor() { super("Your Reader account changed. Refresh your shelf before continuing."); this.code = "READER_ACCOUNT_CHANGED"; }
}
async function verifyReader(reader) {
  const current = await resolveTbrReader();
  if (current.readerSub !== reader.readerSub) throw new TbrAccountChangedError();
  return current;
}
async function request(reader, method, body, cursor) {
  if (!API_BASE) throw new Error("Your TBR shelf is not available yet. Please try again later.");
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try {
    const current = await verifyReader(reader);
    if (controller.signal.aborted) throw Object.assign(new Error(), { name: "AbortError" });
    const response = await fetch(`${API_BASE.replace(/\/$/, "")}/tbr${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, {
      method, signal: controller.signal,
      headers: { Authorization: `Bearer ${current.idToken}`, ...(body ? { "Content-Type": "application/json" } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (response.status === 401 || response.status === 403) throw new TbrAuthError();
    if (!response.ok) throw new Error("Your shelf could not be updated or loaded. Please try again.");
    const data = await response.json();
    await verifyReader(reader);
    if (controller.signal.aborted) throw Object.assign(new Error(), { name: "AbortError" });
    return data;
  } catch (error) {
    if (error.name === "AbortError") throw new Error("Your shelf request timed out. Please try again.");
    throw error;
  } finally { clearTimeout(timer); }
}
const safeUrl = (value, optional = false) => {
  if (optional && (value == null || value === "")) return true;
  if (typeof value !== "string" || value.length > 2048 || /[\\\s]/.test(value)) return false;
  try { const url = new URL(value); return url.protocol === "https:" && !url.username && !url.password; } catch { return false; }
};
const isBook = book => book && typeof book.id === "string" && /^[a-f0-9]{64}$/.test(book.id) &&
  typeof book.title === "string" && Boolean(book.title.trim()) && typeof book.authorName === "string" &&
  safeUrl(book.productUrl) && safeUrl(book.coverImageUrl, true) &&
  (book.savedAt == null || (typeof book.savedAt === "string" && Number.isFinite(Date.parse(book.savedAt)))) &&
  (book.price === null || (typeof book.price === "number" && Number.isFinite(book.price) && book.price >= 0));
export async function listTbr(reader) {
  const books = new Map();
  const seen = new Set();
  let cursor;
  do {
    const data = await request(reader, "GET", null, cursor);
    if (!Array.isArray(data.books) || !data.books.every(isBook)) throw new Error("Your shelf could not be loaded. Please try again.");
    data.books.forEach(book => books.set(book.id, book));
    cursor = data.nextCursor;
    if (cursor && (typeof cursor !== "string" || !/^[a-f0-9]{64}$/.test(cursor) || seen.has(cursor))) throw new Error("Your shelf could not be loaded. Please try again.");
    seen.add(cursor);
  } while (cursor);
  return [...books.values()].sort((a, b) => (b.savedAt || "").localeCompare(a.savedAt || ""));
}
export async function saveTbr(reader, book) {
  const data = await request(reader, "POST", { book });
  if (!isBook(data.book)) throw new Error("The save could not be confirmed. Please refresh your shelf.");
  return data.book;
}
export async function deleteTbr(reader, id) {
  const data = await request(reader, "DELETE", { id });
  if (data?.id !== id) throw new Error("Removal could not be confirmed. Refresh your shelf before trying again.");
}
